/**
 * Styled components for CopyableValue components
 * Extracted from TransactionHistory and generalized for reuse
 */

import styled from 'styled-components';
import type { TooltipPosition, CopyableValueVariant } from './types';

// ============================================================================
// Core Copyable Value Components
// ============================================================================

/**
 * Wrapper for the copyable value component
 * Uses ::before pseudo-element for expanded hover area without affecting layout centering
 * This ensures headers align perfectly with icon content below
 */
export const CopyableValueWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  /* Create expanded clickable/hoverable area without affecting layout */
  &::before {
    content: '';
    position: absolute;
    inset: -16px -18px;  /* Expands hit area without shifting icon position */
  }

  /* Tablet: slightly increase hover area */
  @media (min-width: 768px) {
    &::before {
      inset: -17px -19px;
    }
  }

  /* Desktop: maximum hover area for easy interaction */
  @media (min-width: 1024px) {
    &::before {
      inset: -18px -20px;
    }
  }
`;

/**
 * Trigger button for copyable value
 * Responsive sizing with variant support
 */
export const CopyableValueTrigger = styled.div<{
  $copied?: boolean;
  $variant?: CopyableValueVariant;
  $disabled?: boolean;
}>`
  position: relative;
  z-index: 1;  /* Above the wrapper's ::before pseudo-element for proper hover states */
  display: flex;
  align-items: center;
  justify-content: center;
  /* Responsive icon size */
  width: 28px;
  height: 28px;
  background: ${props => props.$copied ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' : '#f1f5f9'};
  border-radius: 0.375rem;
  color: ${props => props.$copied ? '#16a34a' : '#64748b'};
  font-size: 0.7rem;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  border: 1px solid ${props => props.$copied ? '#86efac' : 'rgba(0, 0, 0, 0.04)'};
  flex-shrink: 0;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    background: ${props => props.$disabled
      ? '#f1f5f9'
      : props.$copied
        ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
        : 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'};
    color: ${props => props.$disabled ? '#64748b' : props.$copied ? '#16a34a' : '#475569'};
    transform: ${props => props.$disabled ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  }

  &:active {
    transform: ${props => props.$disabled ? 'none' : 'scale(0.95)'};
  }

  .main-icon {
    width: 13px;
    height: 13px;
    transition: all 0.2s ease;
  }

  /* Tablet: slightly larger icons */
  @media (min-width: 768px) {
    width: 30px;
    height: 30px;
    border-radius: 0.4rem;

    .main-icon {
      width: 14px;
      height: 14px;
    }
  }

  /* Laptop/Desktop: full size icons */
  @media (min-width: 1024px) {
    width: 34px;
    height: 34px;
    border-radius: 0.5rem;

    .main-icon {
      width: 15px;
      height: 15px;
    }
  }

  .copy-indicator {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 14px;
    height: 14px;
    background: white;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.2s ease;

    svg {
      width: 9px;
      height: 9px;
      color: #6366f1;
    }
  }

  &:hover .copy-indicator {
    opacity: ${props => props.$disabled ? 0 : 1};
    transform: ${props => props.$disabled ? 'scale(0.8)' : 'scale(1)'};
  }
`;

/**
 * Badge shown when value is empty
 */
export const EmptyStateBadge = styled.span<{ $size?: 'sm' | 'md' | 'lg' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Responsive sizing - matches icon triggers */
  width: ${props => props.$size === 'sm' ? '24px' : props.$size === 'lg' ? '38px' : '28px'};
  height: ${props => props.$size === 'sm' ? '24px' : props.$size === 'lg' ? '38px' : '28px'};
  font-size: 0.875rem;
  color: #cbd5e1;
  font-weight: 300;

  @media (min-width: 768px) {
    width: ${props => props.$size === 'sm' ? '26px' : props.$size === 'lg' ? '40px' : '30px'};
    height: ${props => props.$size === 'sm' ? '26px' : props.$size === 'lg' ? '40px' : '30px'};
    font-size: 0.9rem;
  }

  @media (min-width: 1024px) {
    width: ${props => props.$size === 'sm' ? '30px' : props.$size === 'lg' ? '44px' : '34px'};
    height: ${props => props.$size === 'sm' ? '30px' : props.$size === 'lg' ? '44px' : '34px'};
    font-size: 1rem;
  }
`;

// ============================================================================
// Portal Tooltip System
// ============================================================================

/**
 * Base tooltip container - portal-rendered with fixed positioning
 */
export const PortalTooltipBubble = styled.div<{
  $visible: boolean;
  $position: TooltipPosition;
  $variant?: 'default' | 'success';
  $maxWidth?: number;
}>`
  position: fixed;
  top: ${props => props.$position.top}px;
  left: ${props => props.$position.left}px;
  transform: translateY(${props => props.$visible ? '0' : '4px'});
  padding: 0.625rem 0.875rem;
  background: ${props => props.$variant === 'success'
    ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
    : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'};
  color: white;
  border-radius: 0.5rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.5;
  text-transform: lowercase;
  z-index: ${props => props.$variant === 'success' ? 100000 : 99999};
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1),
              visibility 0.2s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.$variant === 'success'
    ? '0 4px 16px rgba(22, 163, 74, 0.3)'
    : '0 4px 20px rgba(0, 0, 0, 0.25)'};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  min-width: 180px;
  max-width: ${props => props.$maxWidth ?? 280}px;
  white-space: normal;
  word-wrap: break-word;

  &::after {
    content: '';
    position: absolute;
    ${props => props.$position.placement === 'above' ? 'bottom: -5px;' : 'top: -5px;'}
    left: ${props => props.$position.arrowLeft}px;
    transform: rotate(45deg);
    width: 10px;
    height: 10px;
    background: ${props => props.$variant === 'success' ? '#15803d' : '#0f172a'};
  }
`;

/**
 * Tooltip content layout - horizontal row
 */
export const TooltipRow = styled.div<{ $justify?: 'start' | 'between' | 'center' }>`
  display: flex;
  align-items: center;
  justify-content: ${props =>
    props.$justify === 'between' ? 'space-between' :
    props.$justify === 'center' ? 'center' :
    'flex-start'};
  gap: 0.5rem;
  width: 100%;
`;

/**
 * Tooltip content layout - vertical stack
 */
export const TooltipStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
`;

/**
 * Tooltip label text
 */
export const TooltipLabel = styled.span`
  font-size: 0.6rem;
  color: #64748b;
  text-transform: lowercase;
  letter-spacing: 0.03em;
  font-weight: 600;
`;

/**
 * Tooltip value text
 */
export const TooltipValue = styled.span<{ $mono?: boolean }>`
  color: #e2e8f0;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  font-family: ${props => props.$mono
    ? "'SF Mono', 'Consolas', 'Monaco', monospace"
    : 'inherit'};
`;

/**
 * Tooltip hint badge (e.g., "click to copy")
 */
export const TooltipHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6rem;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.15);
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-weight: 500;
  text-transform: lowercase;

  svg {
    width: 9px;
    height: 9px;
  }
`;

/**
 * Tooltip divider line
 */
export const TooltipDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, #475569 50%, transparent 100%);
  margin: 0.375rem 0;
`;

/**
 * Tooltip muted text (for empty states)
 */
export const TooltipMuted = styled.span`
  color: #94a3b8;
  font-style: italic;
  font-size: 0.7rem;
`;

// ============================================================================
// Member Preview Components
// ============================================================================

/**
 * Member preview container
 */
export const MemberPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding-top: 0.25rem;
`;

/**
 * Member avatar circle
 */
export const MemberAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    color: white;
  }
`;

/**
 * Member info container
 */
export const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1;
`;

/**
 * Member name text
 */
export const MemberName = styled.span`
  font-size: 0.75rem;
  color: #e2e8f0;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

/**
 * Member email text
 */
export const MemberEmail = styled.span`
  font-size: 0.625rem;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

// ============================================================================
// System Preview Components
// ============================================================================

/**
 * System preview container
 */
export const SystemPreview = styled.div`
  display: flex;
  align-items: center;
  /* Multi-model turns render one badge per model (parity plan Phase 0) —
     wrap instead of overflowing the tooltip bubble. */
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.25rem;
`;

/**
 * Helper to check if a hex color is dark (for determining text color)
 */
const isColorDark = (hex: string): boolean => {
  const color = hex.replace('#', '');
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

/**
 * System badge - styled model name display
 * Dark colors (like Perplexity black) use solid background with white text
 * Light colors use transparent background with colored text
 */
export const SystemBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: 0.4rem;
  font-size: 0.7rem;
  font-weight: 600;
  font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
  letter-spacing: -0.02em;
  background: ${props => isColorDark(props.$color) ? props.$color : `${props.$color}20`};
  color: ${props => isColorDark(props.$color) ? '#ffffff' : props.$color};
  border: 1px solid ${props => isColorDark(props.$color) ? props.$color : `${props.$color}30`};
  white-space: nowrap;
`;

/**
 * System color dot - provider indicator
 */
export const SystemColorDot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${props => props.$color};
`;
